import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { Plus, Trash2, Edit } from 'lucide-react';
import { apiService } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { normalizeUserRole } from '@/lib/roles';

const canDeleteSalaryRecords = (role: string) => role === 'admin' || role === 'hr';

interface EmployeeSalaryManagerProps {
  employeeId: string;
  employeeName: string;
}

export const EmployeeSalaryManager = ({ employeeId, employeeName }: EmployeeSalaryManagerProps) => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [salaries, setSalaries] = useState([]);
  const [payrollItems, setPayrollItems] = useState([]);
  const [salaryScales, setSalaryScales] = useState([]);
  const [allowanceCatalog, setAllowanceCatalog] = useState<any[]>([]);
  const [deductionCatalog, setDeductionCatalog] = useState<any[]>([]);
  const [monthlyAllowances, setMonthlyAllowances] = useState([]);
  const [monthlyDeductions, setMonthlyDeductions] = useState([]);

  // Form states
  const [salaryForm, setSalaryForm] = useState({
    salary_scale_id: '',
    basic_pay: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: ''
  });

  const [payrollItemForm, setPayrollItemForm] = useState({
    payroll_item_id: '',
    custom_value: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: ''
  });

  const [monthlyAllowanceForm, setMonthlyAllowanceForm] = useState({
    name: '',
    type: 'fixed',
    value: '',
    percentage_base: '',
    month: new Date().toISOString().slice(0, 7) + '-01',
    months_to_apply: '1'
  });

  const [monthlyDeductionForm, setMonthlyDeductionForm] = useState({
    name: '',
    type: 'fixed',
    value: '',
    percentage_base: '',
    month: new Date().toISOString().slice(0, 7) + '-01',
    months_to_apply: '1'
  });

  const buildMonthSeries = (firstMonth: string, count: number) => {
    const start = new Date(`${firstMonth.slice(0, 7)}-01T00:00:00`);
    return Array.from({ length: count }, (_, index) => {
      const current = new Date(start);
      current.setMonth(start.getMonth() + index);
      return `${current.toISOString().slice(0, 7)}-01`;
    });
  };

  useEffect(() => {
    loadData();
  }, [employeeId]);

  const unwrapApiList = (response: any) => {
    const raw = response?.data ?? response ?? [];
    return Array.isArray(raw) ? raw : [];
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [salariesRes, payrollItemsRes, salaryScalesRes, allPayrollItemsRes, allowancesRes, deductionsRes] = await Promise.all([
        apiService.getEmployeeSalaries(employeeId),
        apiService.getEmployeePayrollItems(employeeId),
        apiService.getSalaryScales(user?.cid ?? null),
        Promise.all([apiService.getAllowances(user?.cid ?? null), apiService.getDeductions(user?.cid ?? null)]),
        apiService.getEmployeeAllowances(employeeId),
        apiService.getEmployeeDeductions(employeeId)
      ]);

      setSalaries(unwrapApiList(salariesRes));
      setPayrollItems(unwrapApiList(payrollItemsRes));
      setSalaryScales(unwrapApiList(salaryScalesRes));
      // Settings APIs alias method AS `type` (fixed|percentage), not allowance|deduction — keep lists separate.
      setAllowanceCatalog(unwrapApiList(allPayrollItemsRes[0]));
      setDeductionCatalog(unwrapApiList(allPayrollItemsRes[1]));
      setMonthlyAllowances(unwrapApiList(allowancesRes));
      setMonthlyDeductions(unwrapApiList(deductionsRes));
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load employee salary data',
        variant: 'destructive',
      });
    }
    setLoading(false);
  };

  const handleAddSalary = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...salaryForm,
        employee_id: employeeId,
        basic_pay: parseFloat(salaryForm.basic_pay),
        salary_scale_id: (!salaryForm.salary_scale_id || salaryForm.salary_scale_id === 'none')
          ? null
          : parseInt(salaryForm.salary_scale_id, 10),
        end_date: salaryForm.end_date || null
      };
      await apiService.createEmployeeSalary(payload);
      setSalaryForm({
        salary_scale_id: '',
        basic_pay: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: ''
      });
      loadData();
      toast({
        title: 'Success',
        description: 'Salary added successfully',
      });
    } catch (error) {
      console.error('Error adding salary:', error);
      toast({
        title: 'Error',
        description: 'Failed to add salary',
        variant: 'destructive',
      });
    }
    setLoading(false);
  };

  const handleAddPayrollItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!payrollItemForm.payroll_item_id) {
      toast({
        title: 'Error',
        description: 'Please select a payroll item',
        variant: 'destructive',
      });
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...payrollItemForm,
        employee_id: employeeId,
        payroll_item_id: parseInt(payrollItemForm.payroll_item_id, 10),
        custom_value: payrollItemForm.custom_value ? parseFloat(payrollItemForm.custom_value) : null,
        end_date: payrollItemForm.end_date || null
      };
      await apiService.createEmployeePayrollItem(payload);
      setPayrollItemForm({
        payroll_item_id: '',
        custom_value: '',
        start_date: new Date().toISOString().split('T')[0],
        end_date: ''
      });
      loadData();
      toast({
        title: 'Success',
        description: 'Payroll item added successfully',
      });
    } catch (error) {
      console.error('Error adding payroll item:', error);
      toast({
        title: 'Error',
        description: 'Failed to add payroll item',
        variant: 'destructive',
      });
    }
    setLoading(false);
  };

  const handleDeleteSalary = async (id: string) => {
    if (confirm('Are you sure you want to delete this salary record?')) {
      try {
        await apiService.deleteEmployeeSalary(id, normalizeUserRole(user?.role));
        loadData();
        toast({
          title: 'Success',
          description: 'Salary deleted successfully',
        });
      } catch (error) {
        console.error('Error deleting salary:', error);
        toast({
          title: 'Error',
          description: 'Failed to delete salary',
          variant: 'destructive',
        });
      }
    }
  };

  const handleDeletePayrollItem = async (id: string) => {
    if (confirm('Are you sure you want to remove this payroll item?')) {
      try {
        await apiService.deleteEmployeePayrollItem(id, normalizeUserRole(user?.role));
        loadData();
        toast({
          title: 'Success',
          description: 'Payroll item removed successfully',
        });
      } catch (error) {
        console.error('Error deleting payroll item:', error);
        toast({
          title: 'Error',
          description: 'Failed to remove payroll item',
          variant: 'destructive',
        });
      }
    }
  };

  const getItemTypeColor = (type: string) => {
    return type === 'allowance' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  const getItemMethodText = (item: any) => {
    const method = item.method ?? item.type;
    if (method === 'fixed') {
      return `ZMW ${item.value}`;
    }
    return `${item.value}% of ${item.percentage_base || 'basic'}`;
  };

  const payrollCatalogCount = allowanceCatalog.length + deductionCatalog.length;

  // Monthly Allowance handlers
  const handleAddMonthlyAllowance = async () => {
    if (!monthlyAllowanceForm.name || !monthlyAllowanceForm.value) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    try {
      const monthsToApply = Math.max(1, parseInt(monthlyAllowanceForm.months_to_apply || '1', 10) || 1);
      const months = buildMonthSeries(monthlyAllowanceForm.month, monthsToApply);
      await Promise.all(
        months.map((month) =>
          apiService.createEmployeeAllowance(employeeId, {
            ...monthlyAllowanceForm,
            month,
            employee_id: employeeId,
            value: parseFloat(monthlyAllowanceForm.value)
          })
        )
      );
      loadData();
      setMonthlyAllowanceForm({
        name: '',
        type: 'fixed',
        value: '',
        percentage_base: '',
        month: new Date().toISOString().slice(0, 7) + '-01',
        months_to_apply: '1'
      });
      toast({
        title: 'Success',
        description: `Allowance added for ${monthsToApply} month(s) successfully`,
      });
    } catch (error) {
      console.error('Error adding monthly allowance:', error);
      toast({
        title: 'Error',
        description: 'Failed to add monthly allowance',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteMonthlyAllowance = async (id: string) => {
    if (confirm('Are you sure you want to delete this monthly allowance?')) {
      try {
        await apiService.deleteEmployeeAllowance(employeeId, id);
        loadData();
        toast({
          title: 'Success',
          description: 'Monthly allowance deleted successfully',
        });
      } catch (error) {
        console.error('Error deleting monthly allowance:', error);
        toast({
          title: 'Error',
          description: 'Failed to delete monthly allowance',
          variant: 'destructive',
        });
      }
    }
  };

  // Monthly Deduction handlers
  const handleAddMonthlyDeduction = async () => {
    if (!monthlyDeductionForm.name || !monthlyDeductionForm.value) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    try {
      const monthsToApply = Math.max(1, parseInt(monthlyDeductionForm.months_to_apply || '1', 10) || 1);
      const months = buildMonthSeries(monthlyDeductionForm.month, monthsToApply);
      await Promise.all(
        months.map((month) =>
          apiService.createEmployeeDeduction(employeeId, {
            ...monthlyDeductionForm,
            month,
            employee_id: employeeId,
            value: parseFloat(monthlyDeductionForm.value)
          })
        )
      );
      loadData();
      setMonthlyDeductionForm({
        name: '',
        type: 'fixed',
        value: '',
        percentage_base: '',
        month: new Date().toISOString().slice(0, 7) + '-01',
        months_to_apply: '1'
      });
      toast({
        title: 'Success',
        description: `Deduction added for ${monthsToApply} month(s) successfully`,
      });
    } catch (error) {
      console.error('Error adding monthly deduction:', error);
      toast({
        title: 'Error',
        description: 'Failed to add monthly deduction',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteMonthlyDeduction = async (id: string) => {
    if (confirm('Are you sure you want to delete this monthly deduction?')) {
      try {
        await apiService.deleteEmployeeDeduction(employeeId, id);
        loadData();
        toast({
          title: 'Success',
          description: 'Monthly deduction deleted successfully',
        });
      } catch (error) {
        console.error('Error deleting monthly deduction:', error);
        toast({
          title: 'Error',
          description: 'Failed to delete monthly deduction',
          variant: 'destructive',
        });
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Salary Management - {employeeName}</CardTitle>
          <CardDescription>Manage basic pay, salary scale, and payroll items for this employee</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="salary" className="w-full">
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="salary">Salary Details</TabsTrigger>
              <TabsTrigger value="payroll-items">Payroll Items</TabsTrigger>
              <TabsTrigger value="monthly-items">Monthly Items</TabsTrigger>
            </TabsList>

            <TabsContent value="salary" className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-4">Add New Salary</h3>
                <form onSubmit={handleAddSalary} className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="salary_scale_id">Salary Scale</Label>
                    <Select
                      value={salaryForm.salary_scale_id}
                      onValueChange={(value) => setSalaryForm({ ...salaryForm, salary_scale_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select salary scale" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Scale</SelectItem>
                        {salaryScales.map((scale: any) => (
                          <SelectItem key={scale.id} value={scale.id.toString()}>
                            {scale.scale_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="basic_pay">Basic Pay (ZMW)</Label>
                    <Input
                      id="basic_pay"
                      type="number"
                      step="0.01"
                      value={salaryForm.basic_pay}
                      onChange={(e) => setSalaryForm({ ...salaryForm, basic_pay: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Start Date</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={salaryForm.start_date}
                      onChange={(e) => setSalaryForm({ ...salaryForm, start_date: e.target.value })}
                      required
                    />
                  </div>
                  {/* <div className="space-y-2">
                    <Label htmlFor="end_date">End Date (Optional)</Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={salaryForm.end_date}
                      onChange={(e) => setSalaryForm({ ...salaryForm, end_date: e.target.value })}
                    />
                  </div> */}
                  <div className="flex items-end">
                    <Button type="submit" disabled={loading}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Salary
                    </Button>
                  </div>
                </form>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold mb-4">Salary History</h3>
                <div className="space-y-2">
                  {salaries.map((salary: any) => (
                    <div key={salary.id} className="flex items-center justify-between border p-3 rounded">
                      <div className="flex items-center gap-4">
                        <div>
                          <span className="font-medium">ZMW {salary.basic_pay.toLocaleString()}</span>
                          {salary.scale_name && (
                            <Badge variant="outline" className="ml-2">{salary.scale_name}</Badge>
                          )}
                        </div>
                        <div className="text-sm text-gray-600">
                          {salary.start_date} {salary.end_date && `- ${salary.end_date}`}
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteSalary(salary.id)}
                        aria-label="Delete salary record"
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="payroll-items" className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-4">Add Payroll Item</h3>
                <form onSubmit={handleAddPayrollItem} className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="payroll_item_id">Payroll Item</Label>
                    <Select
                      value={payrollItemForm.payroll_item_id}
                      onValueChange={(value) => setPayrollItemForm({ ...payrollItemForm, payroll_item_id: value })}
                      disabled={payrollCatalogCount === 0}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={payrollCatalogCount === 0 ? 'No payroll items in Settings' : 'Select payroll item'} />
                      </SelectTrigger>
                      <SelectContent>
                        {allowanceCatalog.length > 0 && (
                          <SelectGroup>
                            <SelectLabel>Allowances</SelectLabel>
                            {allowanceCatalog.map((item: any) => (
                              <SelectItem key={`allowance-${item.id}`} value={String(item.id)}>
                                {item.name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        )}
                        {deductionCatalog.length > 0 && (
                          <SelectGroup>
                            <SelectLabel>Deductions</SelectLabel>
                            {deductionCatalog.map((item: any) => (
                              <SelectItem key={`deduction-${item.id}`} value={String(item.id)}>
                                {item.name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* <div className="space-y-2">
                    <Label htmlFor="custom_value">Custom Value (Optional)</Label>
                    <Input
                      id="custom_value"
                      type="number"
                      step="0.01"
                      value={payrollItemForm.custom_value}
                      onChange={(e) => setPayrollItemForm({ ...payrollItemForm, custom_value: e.target.value })}
                      placeholder="Override default value"
                    />
                  </div> */}
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Start Date</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={payrollItemForm.start_date}
                      onChange={(e) => setPayrollItemForm({ ...payrollItemForm, start_date: e.target.value })}
                      required
                    />
                  </div>
                  {/* <div className="space-y-2">
                    <Label htmlFor="end_date">End Date (Optional)</Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={payrollItemForm.end_date}
                      onChange={(e) => setPayrollItemForm({ ...payrollItemForm, end_date: e.target.value })}
                    />
                  </div> */}
                  <div className="flex items-end">
                    <Button type="submit" disabled={loading}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Item
                    </Button>
                  </div>
                </form>
              </div>

              <Separator />

              <div>
                <h3 className="text-lg font-semibold mb-4">Current Payroll Items</h3>
                <div className="space-y-2">
                  {payrollItems.map((item: any) => (
                    <div key={item.id} className="flex items-center justify-between border p-3 rounded">
                      <div className="flex items-center gap-4">
                        <Badge className={getItemTypeColor(item.type)}>
                          {item.type.toUpperCase()}
                        </Badge>
                        <div>
                          <span className="font-medium">{item.name}</span>
                          <div className="text-sm text-gray-600">
                            {getItemMethodText(item)}
                            {item.is_mandatory && (
                              <Badge variant="outline" className="ml-2 text-xs">Mandatory</Badge>
                            )}
                          </div>
                        </div>
                        <div className="text-sm text-gray-600">
                          {item.start_date} {item.end_date && `- ${item.end_date}`}
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeletePayrollItem(item.id)}
                        aria-label="Remove payroll item"
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="monthly-items" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Monthly Allowances */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Monthly Allowances</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Monthly allowances are always included in PAYE taxable income when payroll is processed.
                  </p>
                  <form onSubmit={(e) => { e.preventDefault(); handleAddMonthlyAllowance(); }} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="allowance_name">Allowance Name</Label>
                      <Input
                        id="allowance_name"
                        value={monthlyAllowanceForm.name}
                        onChange={(e) => setMonthlyAllowanceForm({ ...monthlyAllowanceForm, name: e.target.value })}
                        placeholder="e.g., Overtime, Bonus"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="allowance_type">Type</Label>
                      <Select
                        value={monthlyAllowanceForm.type}
                        onValueChange={(value) => setMonthlyAllowanceForm({ ...monthlyAllowanceForm, type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fixed">Fixed Amount</SelectItem>
                          <SelectItem value="percentage">Percentage</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="allowance_value">Value</Label>
                      <Input
                        id="allowance_value"
                        type="number"
                        step="0.01"
                        value={monthlyAllowanceForm.value}
                        onChange={(e) => setMonthlyAllowanceForm({ ...monthlyAllowanceForm, value: e.target.value })}
                        placeholder="Amount or percentage"
                        required
                      />
                    </div>
                    {monthlyAllowanceForm.type === 'percentage' && (
                      <div className="space-y-2">
                        <Label htmlFor="allowance_percentage_base">Percentage Base</Label>
                        <Select
                          value={monthlyAllowanceForm.percentage_base}
                          onValueChange={(value) => setMonthlyAllowanceForm({ ...monthlyAllowanceForm, percentage_base: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select base" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="basic">Basic Pay</SelectItem>
                            <SelectItem value="gross">Gross Pay</SelectItem>
                            <SelectItem value="net">Net Pay</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="allowance_month">Month</Label>
                      <Input
                        id="allowance_month"
                        type="month"
                        value={monthlyAllowanceForm.month.slice(0, 7)}
                        onChange={(e) => setMonthlyAllowanceForm({ ...monthlyAllowanceForm, month: e.target.value + '-01' })}
                        required
                      />
                      <p className="text-xs text-muted-foreground">
                        Select the payroll month this item applies to (current or a past month). Future months are allowed only if you intend the item for that period.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="allowance_months_to_apply">Apply for Months</Label>
                      <Input
                        id="allowance_months_to_apply"
                        type="number"
                        min="1"
                        value={monthlyAllowanceForm.months_to_apply}
                        onChange={(e) => setMonthlyAllowanceForm({ ...monthlyAllowanceForm, months_to_apply: e.target.value })}
                        required
                      />
                    </div>
                    <Button type="submit" disabled={loading}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Allowance
                    </Button>
                  </form>

                  <div className="mt-6">
                    <h4 className="font-medium mb-3">Current Monthly Allowances</h4>
                    <div className="space-y-2">
                      {monthlyAllowances.map((allowance: any) => (
                        <div key={allowance.id} className="flex items-center justify-between border p-3 rounded">
                          <div className="flex items-center gap-4">
                            <Badge className="bg-green-100 text-green-800">ALLOWANCE</Badge>
                            <Badge variant="outline" className="bg-amber-50 text-amber-900 border-amber-200 text-xs">
                              PAYE
                            </Badge>
                            <div>
                              <span className="font-medium">{allowance.name}</span>
                              <div className="text-sm text-gray-600">
                                {allowance.type === 'fixed' 
                                  ? `ZMW ${allowance.value}` 
                                  : `${allowance.value}% of ${allowance.percentage_base}`
                                }
                              </div>
                            </div>
                            <div className="text-sm text-gray-600">
                              {new Date(allowance.month).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteMonthlyAllowance(allowance.id)}
                            aria-label="Delete monthly allowance"
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Monthly Deductions */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">Monthly Deductions</h3>
                  <form onSubmit={(e) => { e.preventDefault(); handleAddMonthlyDeduction(); }} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="deduction_name">Deduction Name</Label>
                      <Input
                        id="deduction_name"
                        value={monthlyDeductionForm.name}
                        onChange={(e) => setMonthlyDeductionForm({ ...monthlyDeductionForm, name: e.target.value })}
                        placeholder="e.g., Loan, Advance"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="deduction_type">Type</Label>
                      <Select
                        value={monthlyDeductionForm.type}
                        onValueChange={(value) => setMonthlyDeductionForm({ ...monthlyDeductionForm, type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fixed">Fixed Amount</SelectItem>
                          <SelectItem value="percentage">Percentage</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="deduction_value">Value</Label>
                      <Input
                        id="deduction_value"
                        type="number"
                        step="0.01"
                        value={monthlyDeductionForm.value}
                        onChange={(e) => setMonthlyDeductionForm({ ...monthlyDeductionForm, value: e.target.value })}
                        placeholder="Amount or percentage"
                        required
                      />
                    </div>
                    {monthlyDeductionForm.type === 'percentage' && (
                      <div className="space-y-2">
                        <Label htmlFor="deduction_percentage_base">Percentage Base</Label>
                        <Select
                          value={monthlyDeductionForm.percentage_base}
                          onValueChange={(value) => setMonthlyDeductionForm({ ...monthlyDeductionForm, percentage_base: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select base" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="basic">Basic Pay</SelectItem>
                            <SelectItem value="gross">Gross Pay</SelectItem>
                            <SelectItem value="net">Net Pay</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="deduction_month">Month</Label>
                      <Input
                        id="deduction_month"
                        type="month"
                        value={monthlyDeductionForm.month.slice(0, 7)}
                        onChange={(e) => setMonthlyDeductionForm({ ...monthlyDeductionForm, month: e.target.value + '-01' })}
                        required
                      />
                      <p className="text-xs text-muted-foreground">
                        Select the payroll month this item applies to (current or a past month). Future months are allowed only if you intend the item for that period.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="deduction_months_to_apply">Apply for Months</Label>
                      <Input
                        id="deduction_months_to_apply"
                        type="number"
                        min="1"
                        value={monthlyDeductionForm.months_to_apply}
                        onChange={(e) => setMonthlyDeductionForm({ ...monthlyDeductionForm, months_to_apply: e.target.value })}
                        required
                      />
                    </div>
                    <Button type="submit" disabled={loading}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Deduction
                    </Button>
                  </form>

                  <div className="mt-6">
                    <h4 className="font-medium mb-3">Current Monthly Deductions</h4>
                    <div className="space-y-2">
                      {monthlyDeductions.map((deduction: any) => (
                        <div key={deduction.id} className="flex items-center justify-between border p-3 rounded">
                          <div className="flex items-center gap-4">
                            <Badge className="bg-red-100 text-red-800">DEDUCTION</Badge>
                            <div>
                              <span className="font-medium">{deduction.name}</span>
                              <div className="text-sm text-gray-600">
                                {deduction.type === 'fixed' 
                                  ? `ZMW ${deduction.value}` 
                                  : `${deduction.value}% of ${deduction.percentage_base}`
                                }
                              </div>
                            </div>
                            <div className="text-sm text-gray-600">
                              {new Date(deduction.month).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteMonthlyDeduction(deduction.id)}
                            aria-label="Delete monthly deduction"
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};
